/**
 * As entregas de EPI da empresa — a ficha NR-6 de cada funcionário, com a situação da assinatura.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Chip, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ActionButton, DataTable, PageShell, type DataTableColumn } from '../../../components';
import entregaDeEpiService from '../../../services/entregaDeEpiService';
import {
  COR_DA_ENTREGA,
  ROTULO_DA_ENTREGA,
  ROTULO_DO_TIPO,
  STATUS_DA_ENTREGA,
  dataDaEntrega,
  type EntregaDeEpi,
  type StatusDaEntregaDeEpi,
} from '../../../types/entregaDeEpi';

const colunas: DataTableColumn<EntregaDeEpi>[] = [
  { id: 'funcionario', label: 'Funcionário', primary: true, render: (e) => e.funcionario?.name ?? `#${e.employeeId}` },
  { id: 'tipo', label: 'Tipo', render: (e) => ROTULO_DO_TIPO[e.tipo] },
  {
    id: 'itens',
    label: 'EPIs',
    hideOnMobile: true,
    truncate: true,
    render: (e) => e.itens.map((i) => `${i.quantidade}× ${i.nomeDoEpi}`).join(', '),
  },
  { id: 'entregueEm', label: 'Data', render: (e) => dataDaEntrega(e.entregueEm) },
  {
    id: 'status',
    label: 'Situação',
    render: (e) => <Chip size="small" color={COR_DA_ENTREGA[e.status]} label={ROTULO_DA_ENTREGA[e.status]} />,
  },
];

export const EntregaDeEpiListPage: React.FC = () => {
  const navigate = useNavigate();

  const [entregas, setEntregas] = useState<EntregaDeEpi[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(20);
  const [filtro, setFiltro] = useState<StatusDaEntregaDeEpi | ''>('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resultado = await entregaDeEpiService.listar({ ...(filtro ? { status: filtro } : {}), pagina: pagina + 1, limite: porPagina });
      setEntregas(resultado.entregas);
      setTotal(resultado.total);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar as entregas.');
    } finally {
      setCarregando(false);
    }
  }, [filtro, pagina, porPagina]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'EPIs', path: '/epis' }, { label: 'Entregas' }]}
      title="Entregas de EPI"
      subtitle="Ficha NR-6 de cada funcionário: o que foi entregue, trocado ou devolvido, e a assinatura"
      actions={
        <ActionButton
          acao="epi_entrega.create"
          rotulo="Registrar entrega"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/epis/entregas/nova')}
        >
          Registrar entrega
        </ActionButton>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <FormControl size="small" sx={{ mb: 2, minWidth: 240 }}>
        <InputLabel id="filtro-entrega">Situação</InputLabel>
        <Select
          labelId="filtro-entrega"
          label="Situação"
          value={filtro}
          onChange={(e) => {
            setFiltro(e.target.value as StatusDaEntregaDeEpi | '');
            setPagina(0);
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {STATUS_DA_ENTREGA.map((s) => (
            <MenuItem key={s} value={s}>
              {ROTULO_DA_ENTREGA[s]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <DataTable
        columns={colunas}
        rows={entregas}
        getRowId={(e) => e.id}
        loading={carregando}
        onRowClick={(e) => navigate(`/epis/entregas/${e.id}`)}
        filtersActive={filtro !== ''}
        onClearFilters={() => setFiltro('')}
        pagination={{
          page: pagina,
          rowsPerPage: porPagina,
          totalCount: total,
          onPageChange: setPagina,
          onRowsPerPageChange: (n) => {
            setPorPagina(n);
            setPagina(0);
          },
        }}
        emptyState={{
          title: 'Nenhuma entrega de EPI registrada nesta empresa',
          description:
            'Registre a entrega, a troca ou a devolução de EPI a um funcionário. A ficha NR-6 é gerada na hora e pode ir para o celular dele assinar.',
        }}
      />
    </PageShell>
  );
};

export default EntregaDeEpiListPage;
